import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Calendar,
  MapPin,
  TrendingUp,
  Star,
  Users,
  Video,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Clapperboard,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
  PlayCircle,
  Award,
  Target,
} from "lucide-react";
import woodFloorImage from "@/assets/wood-floor.jpg";
import heroTeamPhoto from "@/assets/hero-team-photo.png";
import circuitActionPhoto from "@/assets/dsc00568.jpg";
import leagueFormatDunk from "@/assets/league-format-dunk.jpg";
import leagueFeesPhoto from "@/assets/league-fees-photo.png";
import eventFormatPhoto from "@/assets/event-format-photo.jpg";
import openRunsPhoto from "@/assets/open-runs-photo.jpg";
import larryHughesEliteCamp from "@/assets/larry-hughes-elite-camp.jpg";
import collegeCoachesMay3 from "@/assets/college_coaches_may3_websize.jpg";
import collegeCoachesMay7 from "@/assets/college_coaches_may7_websize_1.jpg";
import collegeCoachesOSL from "@/assets/OSL-3368_websize.jpg";
import collegeCoachesKC from "@/assets/colleges_kc_july1_websize.jpg";
import collegeCoachesChicago from "@/assets/colleges_chicago4_websize.jpg";
import marquetteLogo from "@/assets/marquette-logo.png";
import cjRivers from "@/assets/cj-rivers.jpg";
import druryLogo from "@/assets/drury-logo.png";
import chrisFoster from "@/assets/chris-foster.jpg";
import calStateFullertonLogo from "@/assets/cal-state-fullerton-logo.png";
import ryanHamm from "@/assets/ryan-hamm.jpg";
import iowaLogo from "@/assets/iowa-logo.png";
import connorWheeler from "@/assets/connor-wheeler.jpg";
import alumniCollage from "@/assets/alumni-collage.png";
import alumniBackground from "@/assets/alumni-background.jpg";
import awardOne from "@/assets/award-one.jpg";
import awardTwo from "@/assets/award-two.jpg";
import awardThree from "@/assets/award-three.jpg";
import awardFour from "@/assets/award-four.jpg";
import awardFive from "@/assets/award-five.jpg";
import awardSix from "@/assets/award-six.jpg";
import awardSeven from "@/assets/award-seven.jpg";
import awardEight from "@/assets/award-eight.jpg";
import leagueFormatNew from "@/assets/league-format-new.png";
import leagueFeesNew from "@/assets/league-fees-new.png";
import eventFormatNew from "@/assets/event-format-new.png";
import openRunsNew from "@/assets/open-runs-new.png";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { UpcomingEvents } from "@/components/hoops/UpcomingEvents";

export default function HoopsBoysCircuit() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={heroTeamPhoto}
            alt="RecruitLook Hoops basketball team celebrating"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl lg:text-6xl font-black mb-4 leading-tight text-center text-secondary">
            The <span className="text-white">#1</span> Independent Hoops Circuit is BACK!
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mb-10 text-center mx-auto">
            1,000+ College Coaches attended a RecruitLook Hoops event in person to see more than 2,500+ AAU teams compete in 2025. The best play here!
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <StatCard value="1,000+" label="College Coaches" />
            <StatCard value="2,500+" label="AAU Teams" />
            <StatCard value="40,000,000+" label="Impressions" />
            <StatCard value="75k+" label="Social Followers" />
            <StatCard value="500+" label="Videos Created" />
            <StatCard value="50k+" label="Spectators" />
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="pt-8 pb-8 md:pt-12 md:pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 flex-wrap">
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/boys-circuit">BOYS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/girls-circuit">GIRLS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/larry-hughes-elite-camp">ELITE CAMP</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/juniors-circuit">JUNIORS CIRCUIT</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">UPCOMING EVENTS</Badge>
            <h2 className="text-3xl lg:text-5xl font-black mb-4">Boys Circuit Events</h2>
            <p className="text-xl text-gray-600">Join us at exclusive basketball tournaments, camps, and showcases</p>
          </div>
          <UpcomingEvents sportSlug="basketball" sportName="basketball" gender="Men" />
        </div>
      </section>

      {/* Quick info cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3 text-secondary"><Users className="w-5 h-5" /><span className="font-black">15u–17u Teams</span></div>
            <p className="text-gray-700">Competitive divisions, elite venues, national media coverage, and real recruiting exposure.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3 text-secondary"><Calendar className="w-5 h-5" /><span className="font-black">Live + Non‑Live Periods</span></div>
            <p className="text-gray-700">Three NCAA Live Periods plus select non‑live showcases count toward league standings.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3 text-secondary"><Trophy className="w-5 h-5" /><span className="font-black">Finale Championships</span></div>
            <p className="text-gray-700">Top teams compete at The Finale for the RecruitLook Hoops Championship title.</p>
          </div>
        </div>
      </section>

      {/* College Coaches */}
      <section className="py-16 bg-gradient-to-br from-primary to-primary/90 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">COLLEGE COACHES</Badge>
            <h2 className="text-3xl lg:text-5xl font-black mb-4">College Coaches</h2>
            <p className="text-xl lg:text-2xl text-white/90 font-semibold">Over 1000 College Coaches attended a RecruitLook event in 2025</p>
          </div>
          
          {/* Image Collage */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="rounded-xl overflow-hidden">
              <img 
                src={collegeCoachesMay3} 
                alt="College coach at RecruitLook event" 
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden">
              <img 
                src={collegeCoachesMay7} 
                alt="College coaches watching RecruitLook tournament" 
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden">
              <img 
                src={collegeCoachesOSL} 
                alt="College coaches at RecruitLook basketball event" 
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden">
              <img 
                src={collegeCoachesKC} 
                alt="College coaches at Kansas City RecruitLook event" 
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden col-span-2 md:col-span-3 lg:col-span-1">
              <img 
                src={collegeCoachesChicago} 
                alt="College coaches at Chicago RecruitLook event" 
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
          
          {/* Coach Testimonials Slider */}
          <div className="mt-16">
            <Carousel
              className="mx-auto max-w-4xl relative"
              plugins={[
                Autoplay({
                  delay: 5000,
                }),
              ]}
            >
              <CarouselContent>
                <CarouselItem>
                  <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="flex-shrink-0">
                      <img 
                        src={cjRivers} 
                        alt="CJ Rivers, Marquette University" 
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <blockquote className="text-lg md:text-xl text-white/95 mb-4 leading-relaxed">
                        "I attend Recruitlook events for the hospitality of the event organizers, combined with the collection of talented players that are showcased on their circuit. They do a great job of organizing and communicating"
                      </blockquote>
                      <div className="flex items-center justify-center md:justify-start gap-4">
                        <img 
                          src={marquetteLogo} 
                          alt="Marquette University logo" 
                          className="w-12 h-12 object-contain"
                        />
                        <div>
                          <div className="font-black text-white">CJ Rivers</div>
                          <div className="text-white/80">Marquette University</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
                
                <CarouselItem>
                  <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="flex-shrink-0">
                      <img 
                        src={chrisFoster} 
                        alt="Chris Foster, Drury University" 
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <blockquote className="text-lg md:text-xl text-white/95 mb-4 leading-relaxed">
                        "One of the best facilities in the Midwest at their KC live period. The hospitality and location were great. There is high-level competition at Recruitlook events and a lot of recruitable players on the circuit for our level."
                      </blockquote>
                      <div className="flex items-center justify-center md:justify-start gap-4">
                        <img 
                          src={druryLogo} 
                          alt="Drury University logo" 
                          className="w-12 h-12 object-contain"
                        />
                        <div>
                          <div className="font-black text-white">Chris Foster</div>
                          <div className="text-white/80">Drury University</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
                
                <CarouselItem>
                  <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="flex-shrink-0">
                      <img 
                        src={ryanHamm} 
                        alt="Ryan Hamm, Cal State Fullerton" 
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <blockquote className="text-lg md:text-xl text-white/95 mb-4 leading-relaxed">
                        "I attend RecruitLook events for several reasons. I can always count on quality matchups between well-respected programs. The communication from the RecruitLook staff and the organization of the event is always professional and timely. I know when a game is supposed to start. There will be 2 quality teams playing with high-character, talented, prospective student-athletes."
                      </blockquote>
                      <div className="flex items-center justify-center md:justify-start gap-4">
                        <img 
                          src={calStateFullertonLogo} 
                          alt="Cal State Fullerton logo" 
                          className="w-12 h-12 object-contain"
                        />
                        <div>
                          <div className="font-black text-white">Ryan Hamm</div>
                          <div className="text-white/80">Cal State Fullerton</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
                
                <CarouselItem>
                  <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="flex-shrink-0">
                      <img 
                        src={connorWheeler} 
                        alt="Connor Wheeler, Iowa" 
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <blockquote className="text-lg md:text-xl text-white/95 mb-4 leading-relaxed">
                        "I attend RecruitLook Hoops events because they are first-class events with very talented potential student-athletes. Great people running the events, look forward to them every year!"
                      </blockquote>
                      <div className="flex items-center justify-center md:justify-start gap-4">
                        <img 
                          src={iowaLogo} 
                          alt="Iowa logo" 
                          className="w-12 h-12 object-contain"
                        />
                        <div>
                          <div className="font-black text-white">Connor Wheeler</div>
                          <div className="text-white/80">Iowa</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>
              
              {/* Navigation Arrows */}
              <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 border-white/30 text-white hover:bg-white/30" />
              <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 border-white/30 text-white hover:bg-white/30" />
              
              {/* Dots/Bullets */}
              <div className="flex justify-center gap-2 mt-6">
                <div className="w-2 h-2 rounded-full bg-white/40"></div>
                <div className="w-2 h-2 rounded-full bg-white/40"></div>
                <div className="w-2 h-2 rounded-full bg-white/40"></div>
                <div className="w-2 h-2 rounded-full bg-white/40"></div>
              </div>
            </Carousel>
            
            {/* Gallery Button */}
            <div className="text-center mt-8">
              <Button
                asChild
                className="bg-secondary hover:bg-secondary/90 text-black font-black px-8 py-4 text-lg"
              >
                <a 
                  href="https://recruitlook.passgallery.com/-collegecoachesatrecruitlookhoops/gallery" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View Our College Coach Gallery
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">HOW IT WORKS</Badge>
            <h2 className="text-3xl lg:text-5xl font-black">How Does It Work?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <PlayCircle className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Play in the events</h3>
              <p className="text-gray-700">Certified Circuit Teams play in a set number of events.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <TrendingUp className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Earn Points</h3>
              <p className="text-gray-700">Teams move up and down the standings based off wins/losses algorithm.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <Target className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">July's Circuit Finale</h3>
              <p className="text-gray-700">Teams will compete to see who gets crowned the RecruitLook Hoops Champion.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <Award className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Larry Hughes Elite Camp</h3>
              <p className="text-gray-700">Top performers will earn invites to the Larry Hughes Elite Camp.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the circuit */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={circuitActionPhoto}
            alt="Basketball players in action during RecruitLook Hoops circuit game"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-8">
            <div>
              <div className="mb-8">
                <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">ABOUT THE CIRCUIT</Badge>
                <h3 className="text-3xl font-black mb-4 text-white">More Important Circuit Information</h3>
                <div className="space-y-4 text-gray-200 leading-relaxed max-w-4xl">
                  <p>We hosted 2,500+ AAU basketball teams in 2025 and over 1,000 college coaches (in-person & on the live streams) throughout the spring and summer.</p>
                  <p>Our circuit teams benefited by playing top-level competition in front of college coaches — giving the coaches the best opportunities for proper player evaluations.</p>
                  <p>The Circuit is open to any team to play in our Open Runs; however, we have a select group that is in our premier division that gets all the benefits of committing to the circuit. Teams will compete in a competitive League and move up and down the standings based on their wins and losses. All roads lead to The Finale where our bracket finale will take place. Teams will be seeded based on their League standings. Winner take all!</p>
                  <p>Top players from the League will be invited to participate in the Larry Hughes Elite Camp. This has quickly become one of the top camps in the country and will feature nationally ranked players from across the country to compete against top RecruitLook Hoops Circuit players.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-8">
            <div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="bg-secondary text-slate-900 rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-slate-900"><TrendingUp className="w-5 h-5 text-slate-900" /> League Format</h4>
                  <ul className="list-disc pl-6 space-y-2 text-slate-900">
                    <li>Every event that we host prior to the Bracket Finale will be considered a League Session.</li>
                    <li>Teams are required to play in all 3 NCAA Live Periods to be a Certified Circuit Team.</li>
                    <li>Teams will be required to play in at least 2 non-live event hosted by RecruitLook Hoops.</li>
                    <li>Standings will be based on your Live Period records PLUS your best non-live event record.</li>
                  </ul>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4 text-gray-700">
                    <p className="mb-2 font-semibold">Examples:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>If you went 12-0 in live periods, 0-4 in the only non-live period event, then your record is 12-4.</li>
                      <li>If you went 12-0 in live periods. 0-4 and 2-2 in the non-live period events, then your record is 14-2.</li>
                    </ul>
                    <p className="mt-3">Standings are a mix of your wins, quality wins, and power index rating.</p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={leagueFormatNew} alt="Basketball players competing during league play" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={leagueFeesNew} alt="Basketball players helping teammate up during game" className="w-full h-full object-cover" />
                </div>
                <div className="bg-slate-900 text-white rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-white"><Star className="w-5 h-5 text-white" /> League Fees</h4>
                  <ul className="list-disc pl-6 space-y-1 text-white">
                    <li>Prices are per event. However, Certified Circuit Teams will get discounted prices for tournaments.</li>
                    <li>Non-Circuit Teams signing up for the Open Division will receive multi-team discounts for select events.</li>
                    <li>Live Periods = $650</li>
                    <li>Non-Live Periods = $600</li>
                    <li>Circuit teams get $100 off per team, per event.</li>
                  </ul>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="bg-secondary text-slate-900 rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-slate-900"><Calendar className="w-5 h-5 text-slate-900" /> Event Format</h4>
                  <div className="space-y-2 text-slate-900">
                    <p className="font-semibold">Each Event Will Go As Follows:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Each team will play 4 games at the designated showcase location.</li>
                      <li>Each event is a 3-day showcase style tournament. Your schedule is mapped out for the entire weekend.</li>
                      <li>We accommodate schedule requests in the order registration and fees are paid.</li>
                      <li>The Bracket Finale will be in Chicago and another in Kansas City. The top 16 teams in each age division go to Chicago. The rest will go to Kansas City.</li>
                      <li>Teams that do not qualify for the Finale are 100% still eligible to play in the event in the Open Division against other quality teams.</li>
                    </ul>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={eventFormatNew} alt="Basketball game action during tournament event" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={openRunsNew} alt="Basketball players jumping for the ball during open run game" className="w-full h-full object-cover" />
                </div>
                <div className="bg-slate-900 text-white rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-white"><MapPin className="w-5 h-5 text-white" /> Open Runs</h4>
                  <div className="space-y-3">
                    <p>All events are also Open Runs. We don't want to limit the number of teams that are looking for quality events to play in, so we will offer Open Run Division for teams to come and play at the same venues. These events will be showcase-style for the Open Run Division.</p>
                    <h5 className="text-lg font-black">First Class</h5>
                    <p>We want to make this a great experience for everyone involved. We want you to be proud of the Circuit that you are in. We know that the best advertising and marketing is word-of-mouth, and we'll strive to make a lasting impression on you. Our team has been running events for decades, and we know that attention to detail, big or small, is key when running an event. We'll make sure that your time on the RecruitLook Hoops Circuit is enjoyable.</p>
                  </div>
                </div>
              </div>


              <div className="mb-4 text-center">
                <h4 className="text-2xl font-black mb-3 flex items-center gap-2 justify-center"><Video className="w-5 h-5 text-secondary" /> Live Streaming</h4>
                <p className="text-gray-700 mb-4">ALL RecruitLook Hoops Events will be Live Streamed through BallerTV for College Coaches and Fans to enjoy!</p>
                <div className="flex justify-center">
                  <Button asChild variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-black">
                    <a href="https://www.ballertv.com/organizations/recruitlook" target="_blank" rel="noopener noreferrer">Visit BallerTV.com/RecruitLook</a>
                  </Button>
                </div>
              </div>
              <div className="flex justify-center my-6">
                <div className="h-1 bg-slate-900 w-3/4 rounded-full"></div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Success At The Next Level */}
      <section className="py-16 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={alumniBackground}
            alt="RecruitLook alumni background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-black mb-4 text-secondary">Success At The Next Level</h2>
            <p className="text-xl lg:text-2xl font-semibold text-white/90">We don't chase trophies. We chase scholarships!</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <div className="text-6xl lg:text-7xl font-black text-secondary mb-4">28</div>
                <div className="text-xl lg:text-2xl font-bold">RecruitLook Circuit Alumni</div>
                <div className="text-lg lg:text-xl text-white/90 mt-2">Played In The 2025 NCAA Tournament</div>
              </div>
            </div>
            
            <div>
              <img 
                src={alumniCollage} 
                alt="RecruitLook League Alumni who played in the 2025 NCAA Tournament" 
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
          
          {/* Award Photos Grid */}
          <div className="mt-16">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <img src={awardOne} alt="RecruitLook Alumni Award 1" className="w-full rounded-lg shadow-lg" />
              <img src={awardTwo} alt="RecruitLook Alumni Award 2" className="w-full rounded-lg shadow-lg" />
              <img src={awardThree} alt="RecruitLook Alumni Award 3" className="w-full rounded-lg shadow-lg" />
              <img src={awardFour} alt="RecruitLook Alumni Award 4" className="w-full rounded-lg shadow-lg" />
              <img src={awardFive} alt="RecruitLook Alumni Award 5" className="w-full rounded-lg shadow-lg" />
              <img src={awardSix} alt="RecruitLook Alumni Award 6" className="w-full rounded-lg shadow-lg" />
              <img src={awardSeven} alt="RecruitLook Alumni Award 7" className="w-full rounded-lg shadow-lg" />
              <img src={awardEight} alt="RecruitLook Alumni Award 8" className="w-full rounded-lg shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Media coverage examples */}
      <section className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">MEDIA COVERAGE</Badge>
            <h3 className="text-3xl lg:text-5xl font-black">Media Coverage Examples</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Star className="w-5 h-5" /> Recruiting Coverage</div>
              <p className="text-gray-700">A quick way to highlight a player that has performed well. We'll tweet before, during, and after events to promote players.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Clapperboard className="w-5 h-5" /> Video Highlights</div>
              <p className="text-gray-700">Our media team is filming games, editing clips, and pushing out highlight videos during the event for more exposure.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><ImageIcon className="w-5 h-5" /> Event Pics</div>
              <p className="text-gray-700">Our media team took pictures of all the teams that competed in RecruitLook Hoops events that were available for download and print.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Larry Hughes Elite Camp */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">ELITE CAMP</Badge>
            <h3 className="text-3xl lg:text-5xl font-black">RecruitLook Hoops Presents Larry Hughes Elite Camp</h3>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h4 className="text-2xl font-black mb-4">Pathway to the Top</h4>
              <p className="text-gray-700 mb-4">Top players from the League are invited to the Larry Hughes Elite Camp, featuring nationally ranked talent from across the country alongside RecruitLook Hoops Circuit standouts.</p>
              <div className="mt-6">
                <h5 className="text-xl font-black mb-2">Larry Hughes Elite Camp</h5>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Started in 2018.</li>
                  <li>This is an invite only camp to 60 of the best high school players each year.</li>
                  <li>Players from 20 different states attended the 2022 camp.</li>
                  <li>National media outlets cover this camp.</li>
                  <li>Players are picking up their first collegiate offers after this camp.</li>
                  <li>Only camp in the country that is partnered with Allen Iverson's group.</li>
                  <li>The camp MVP gets selected to the prestigious Iverson Classic.</li>
                </ul>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src={larryHughesEliteCamp} alt="Larry Hughes Elite Camp group photo showing all participating players and coaches" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-secondary text-black rounded-2xl p-6 text-center shadow">
      <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-2">{value}</div>
      <div className="text-xs sm:text-sm font-semibold text-white break-words">{label}</div>
    </div>
  );
}